import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ExternalLink, Filter, Inbox } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { PlaneRefreshHeader, type RefreshMeta } from "@/components/PlaneRefreshHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface Ticket {
  id: string;
  name: string;
  state: string;
  stateGroup: "backlog" | "unstarted" | "started" | "completed" | "cancelled" | "unknown";
  priority: "urgent" | "high" | "medium" | "low" | "none";
  dueDate?: string;
  url: string;
  projectId?: string;
  projectName?: string;
  projectIdentifier?: string;
  panelProjectId?: string;
  panelProjectName?: string;
  panelProjectSlug?: string;
}

export const Route = createFileRoute("/tickets")({
  component: TicketsPage,
});

const STATE_GROUP_LABEL: Record<Ticket["stateGroup"], string> = {
  backlog: "Backlog",
  unstarted: "Por empezar",
  started: "En progreso",
  completed: "Terminado",
  cancelled: "Cancelado",
  unknown: "—",
};

const STATE_GROUP_BADGE: Record<Ticket["stateGroup"], string> = {
  backlog: "badge-ghost",
  unstarted: "badge-info",
  started: "badge-primary",
  completed: "badge-success",
  cancelled: "badge-warning",
  unknown: "badge-ghost",
};

const PRIORITY_LABEL: Record<Ticket["priority"], string> = {
  urgent: "Urgente",
  high: "Alta",
  medium: "Media",
  low: "Baja",
  none: "Sin prioridad",
};

const PRIORITY_BADGE: Record<Ticket["priority"], string> = {
  urgent: "badge-error",
  high: "badge-warning",
  medium: "badge-info",
  low: "badge-ghost",
  none: "badge-ghost",
};

type StateFilter = "open" | "all" | "closed";

function TicketsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ tickets: Ticket[]; meta: RefreshMeta }>({
    queryKey: ["tickets", "me"],
    queryFn: () => api.get("/tickets/me"),
  });
  const refresh = useMutation({
    mutationFn: () => api.get<{ tickets: Ticket[]; meta: RefreshMeta }>("/tickets/me?refresh=1"),
    onSuccess: (fresh) => qc.setQueryData(["tickets", "me"], fresh),
  });

  const [stateFilter, setStateFilter] = useState<StateFilter>("open");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");

  const tickets = data?.tickets ?? [];

  // Auto-refresh único cuando la primera respuesta llega vacía. Cubre el
  // caso de cache stale tras un sync fallido del cron — el user no
  // tiene que hacer click manual. Si tras el refresh sigue vacío, no
  // reintentamos: hay empleados que efectivamente no tienen tickets.
  const autoRefreshedRef = useRef(false);
  useEffect(() => {
    if (!data || autoRefreshedRef.current) return;
    if (tickets.length === 0 && !refresh.isPending) {
      autoRefreshedRef.current = true;
      refresh.mutate();
    }
  }, [data, tickets.length, refresh]);

  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tickets) {
      const id = t.projectId ?? "";
      const name = t.projectName ?? "(sin proyecto)";
      if (!map.has(id)) map.set(id, name);
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .toSorted((a, b) => a.name.localeCompare(b.name));
  }, [tickets]);

  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      const closed = t.stateGroup === "completed" || t.stateGroup === "cancelled";
      if (stateFilter === "open" && closed) return false;
      if (stateFilter === "closed" && !closed) return false;
      if (projectFilter !== "all" && (t.projectId ?? "") !== projectFilter) return false;
      if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
      return true;
    });
  }, [tickets, stateFilter, projectFilter, priorityFilter]);

  // Resumen sobre los filtros activos — recalculado cuando cambian.
  const summary = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const in3 = new Date(today);
    in3.setDate(in3.getDate() + 3);
    let overdue = 0;
    let upcoming = 0;
    let noDue = 0;
    const byPriority = { urgent: 0, high: 0, medium: 0, low: 0, none: 0 };
    for (const t of filtered) {
      byPriority[t.priority]++;
      if (!t.dueDate) {
        noDue++;
        continue;
      }
      const d = new Date(t.dueDate);
      d.setHours(0, 0, 0, 0);
      const isClosed = t.stateGroup === "completed" || t.stateGroup === "cancelled";
      if (!isClosed && d < today) overdue++;
      else if (!isClosed && d <= in3) upcoming++;
    }
    return { overdue, upcoming, noDue, byPriority };
  }, [filtered]);

  // Agrupar por proyecto. Si el proyecto Plane está mapeado al catálogo
  // del panel preferimos ese nombre — más estable y linkeable. Orden por
  // nombre y dentro de cada grupo por dueDate ascendente (nulls al final).
  const grouped = useMemo(() => {
    const map = new Map<
      string,
      {
        name: string;
        identifier?: string;
        panelProjectId?: string;
        panelProjectSlug?: string;
        planeProjectName?: string;
        items: Ticket[];
      }
    >();
    for (const t of filtered) {
      const key = t.projectId ?? "_none";
      const existing = map.get(key);
      if (existing) {
        existing.items.push(t);
      } else {
        map.set(key, {
          name: t.panelProjectName ?? t.projectName ?? "(sin proyecto)",
          identifier: t.projectIdentifier,
          panelProjectId: t.panelProjectId,
          panelProjectSlug: t.panelProjectSlug,
          planeProjectName: t.panelProjectName ? t.projectName : undefined,
          items: [t],
        });
      }
    }
    for (const g of map.values()) {
      g.items.sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return a.dueDate.localeCompare(b.dueDate);
      });
    }
    return [...map.values()].toSorted((a, b) => a.name.localeCompare(b.name));
  }, [filtered]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="tickets"
        title="Todos tus tickets"
        subtitle="Lo que tienes asignado en Plane, agrupado por proyecto."
        actions={
          <Link to="/" className="btn btn-ghost btn-sm">
            Volver al dashboard
          </Link>
        }
      />

      <PlaneRefreshHeader
        meta={data?.meta ?? null}
        isPending={refresh.isPending}
        onRefresh={() => refresh.mutate()}
      />

      {/* Resumen — recalcula sobre los filtros activos. Los tres primeros
          tiles son los buckets del dashboard ("hoy"); el último es la
          suma total que matchea filtros. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total" value={filtered.length} tone="primary" />
        <StatTile label="Atrasados" value={summary.overdue} tone="error" />
        <StatTile label="Próximos (3 días)" value={summary.upcoming} tone="warning" />
        <StatTile label="Sin fecha" value={summary.noDue} tone="ghost" />
      </div>

      <div className="card-elevated p-3 flex flex-wrap items-center gap-2">
        <Filter className="size-4 text-base-content/50 ml-1" />
        <SegmentedFilter
          value={stateFilter}
          onChange={(v) => setStateFilter(v as StateFilter)}
          options={[
            { value: "open", label: "Abiertos" },
            { value: "all", label: "Todos" },
            { value: "closed", label: "Cerrados" },
          ]}
        />
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          className="select select-bordered select-sm"
          aria-label="Filtrar por proyecto"
        >
          <option value="all">Todos los proyectos</option>
          {projectOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="select select-bordered select-sm"
          aria-label="Filtrar por prioridad"
        >
          <option value="all">Cualquier prioridad</option>
          <option value="urgent">Urgente</option>
          <option value="high">Alta</option>
          <option value="medium">Media</option>
          <option value="low">Baja</option>
          <option value="none">Sin prioridad</option>
        </select>
        <div className="ml-auto text-sm text-base-content/55">
          {data ? `${filtered.length} de ${tickets.length}` : null}
        </div>
      </div>

      {isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      {!isLoading && grouped.length === 0 && (
        <div className="card-elevated py-12 text-center">
          <div className="size-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4">
            <Inbox className="size-6 text-primary" />
          </div>
          <h3 className="font-display font-semibold mb-1">Sin tickets</h3>
          <p className="text-sm text-base-content/60">
            {tickets.length === 0
              ? "No tienes tickets asignados en Plane."
              : "Ningún ticket coincide con los filtros."}
          </p>
        </div>
      )}

      {!isLoading && grouped.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {grouped.map((g) => (
            <ProjectCard key={g.name} group={g} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "primary" | "warning" | "error" | "ghost";
}) {
  const accent = {
    primary: "text-primary",
    warning: "text-warning",
    error: "text-error",
    ghost: "text-base-content/50",
  }[tone];
  const dot = {
    primary: "bg-primary",
    warning: "bg-warning",
    error: "bg-error",
    ghost: "bg-base-300",
  }[tone];
  return (
    <div className="card-elevated p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`size-2 rounded-full ${dot}`} aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
          {label}
        </span>
      </div>
      <div className={`text-3xl font-display font-bold tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

interface ProjectGroup {
  name: string;
  identifier?: string;
  panelProjectId?: string;
  panelProjectSlug?: string;
  /** Si el grupo está mapeado al panel, este es el nombre original en Plane. */
  planeProjectName?: string;
  items: Ticket[];
}

function ProjectCard({ group }: { group: ProjectGroup }) {
  const overdueCount = group.items.filter(
    (t) => t.dueDate && isOverdue(t.dueDate, t.stateGroup),
  ).length;
  const titleNode = group.panelProjectId ? (
    <Link
      to="/projects/$projectId"
      params={{ projectId: group.panelProjectId }}
      onClick={(e) => e.stopPropagation()}
      className="font-display font-semibold truncate hover:text-primary"
    >
      {group.name}
    </Link>
  ) : (
    <span className="font-display font-semibold truncate">{group.name}</span>
  );
  return (
    <details className="card-elevated overflow-hidden group" open>
      <summary className="px-4 py-3 cursor-pointer list-none flex items-center gap-2 hover:bg-base-200/40 transition-colors">
        <ChevronDown className="size-4 text-base-content/40 transition-transform group-open:rotate-0 -rotate-90 shrink-0" />
        <div className="min-w-0 flex-1">
          {titleNode}
          <div className="text-[10px] uppercase tracking-wider text-base-content/45 mt-0.5 flex items-center gap-1.5 flex-wrap">
            {group.identifier && <span>{group.identifier}</span>}
            {group.planeProjectName && (
              <span className="opacity-70 normal-case">· Plane: {group.planeProjectName}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {overdueCount > 0 && (
            <span className="badge badge-error badge-sm" title={`${overdueCount} atrasados`}>
              {overdueCount}
            </span>
          )}
          <span className="badge badge-ghost badge-sm">{group.items.length}</span>
        </div>
      </summary>
      <ul className="divide-y divide-base-300/40 border-t border-base-300/40">
        {group.items.map((t) => (
          <li key={t.id}>
            <a
              href={t.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-start gap-3 px-4 py-2.5 hover:bg-base-200/60 transition-colors group/item"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{t.name}</div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className={`badge badge-xs ${STATE_GROUP_BADGE[t.stateGroup]}`}>
                    {STATE_GROUP_LABEL[t.stateGroup]}
                  </span>
                  {t.priority !== "none" && (
                    <span className={`badge badge-xs ${PRIORITY_BADGE[t.priority]}`}>
                      {PRIORITY_LABEL[t.priority]}
                    </span>
                  )}
                  {t.dueDate && (
                    <span
                      className={
                        "text-xs " +
                        (isOverdue(t.dueDate, t.stateGroup) ? "text-error" : "text-base-content/55")
                      }
                    >
                      {formatDate(t.dueDate)}
                    </span>
                  )}
                </div>
              </div>
              <ExternalLink className="size-3.5 text-base-content/30 group-hover/item:text-primary shrink-0 mt-1" />
            </a>
          </li>
        ))}
      </ul>
    </details>
  );
}

function isOverdue(due: string, stateGroup: Ticket["stateGroup"]): boolean {
  if (stateGroup === "completed" || stateGroup === "cancelled") return false;
  const d = new Date(due);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today;
}

function SegmentedFilter({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="join">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={"join-item btn btn-sm " + (value === o.value ? "btn-primary" : "btn-ghost")}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
